import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { collection, writeBatch, doc, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface StudentRecord {
  enrollmentId: string;
  rollNumber: string;
  name: string;
  class: string;
  section: string;
  house?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  address?: string;
  parentName?: string;
  parentPhone?: string;
  parentEmail?: string;
  bloodGroup?: string;
  transportAvailed?: string;
  aadhaarNumber?: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface BulkUploadProps {
  domain: string;
}

const BulkUpload: React.FC<BulkUploadProps> = ({ domain }) => {
  const [file, setFile] = useState<File | null>(null);
  const [students, setStudents] = useState<StudentRecord[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  const downloadTemplate = () => {
    // Create template data
    const templateData = [
      {
        'Enrollment ID*': 'ENR2025001',
        'Roll Number*': '001',
        'Name*': 'John Doe',
        'Class*': '10',
        'Section*': 'A',
        'House': 'Red',
        'Email': 'john.doe@example.com',
        'Phone': '9876543210',
        'Date of Birth (YYYY-MM-DD)': '2010-05-15',
        'Gender (male/female/other)': 'male',
        'Address': '123 Main Street, City',
        'Parent Name': 'Jane Doe',
        'Parent Phone': '9876543211',
        'Parent Email': 'jane.doe@email.com',
        'Blood Group': 'A+',
        'School Transport Availed (Y/N)': 'Y',
        'Aadhaar Number': '123456789012',
      },
      {
        'Enrollment ID*': 'ENR2025002',
        'Roll Number*': '002',
        'Name*': 'Jane Smith',
        'Class*': '10',
        'Section*': 'A',
        'House': 'Blue',
        'Email': 'jane.smith@example.com',
        'Phone': '9876543220',
        'Date of Birth (YYYY-MM-DD)': '2010-08-22',
        'Gender (male/female/other)': 'female',
        'Address': '456 Park Avenue, City',
        'Parent Name': 'John Smith',
        'Parent Phone': '9876543221',
        'Parent Email': 'john.smith@email.com',
        'Blood Group': 'B+',
        'School Transport Availed (Y/N)': 'N',
        'Aadhaar Number': '987654321098',
      },
    ];

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');

    // Set column widths
    ws['!cols'] = [
      { wch: 18 }, // Enrollment ID
      { wch: 15 }, // Roll Number
      { wch: 20 }, // Name
      { wch: 10 }, // Class
      { wch: 10 }, // Section
      { wch: 12 }, // House
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 25 }, // Date of Birth
      { wch: 25 }, // Gender
      { wch: 30 }, // Address
      { wch: 20 }, // Parent Name
      { wch: 15 }, // Parent Phone
      { wch: 25 }, // Parent Email
      { wch: 15 }, // Blood Group
      { wch: 30 }, // School Transport Availed
      { wch: 18 }, // Aadhaar Number
    ];

    // Download file
    XLSX.writeFile(wb, 'student_bulk_upload_template.xlsx');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setStudents([]);
      setValidationErrors([]);
      setUploadComplete(false);
      parseExcel(selectedFile);
    }
  };

  const parseExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const parsedStudents: StudentRecord[] = [];
        const errors: ValidationError[] = [];
        const enrollmentIdsInFile = new Set<string>();
        const rollNumbersInFile = new Map<string, number>(); // Track roll numbers per class-section

        jsonData.forEach((row: any, index: number) => {
          const rowNumber = index + 2; // +2 because row 1 is header

          // Validate required fields
          const enrollmentId = String(row['Enrollment ID*'] || '').trim();
          const rollNumber = String(row['Roll Number*'] || '').trim();
          const name = String(row['Name*'] || '').trim();
          const studentClass = String(row['Class*'] || '').trim();
          const section = String(row['Section*'] || '').trim();

          if (!enrollmentId) {
            errors.push({ row: rowNumber, field: 'Enrollment ID', message: 'Required field' });
          } else if (enrollmentIdsInFile.has(enrollmentId)) {
            errors.push({ row: rowNumber, field: 'Enrollment ID', message: `Duplicate Enrollment ID: ${enrollmentId}` });
          } else {
            enrollmentIdsInFile.add(enrollmentId);
          }

          if (!rollNumber) {
            errors.push({ row: rowNumber, field: 'Roll Number', message: 'Required field' });
          } else if (studentClass && section) {
            const classKey = `${studentClass}-${section}-${rollNumber}`;
            if (rollNumbersInFile.has(classKey)) {
              errors.push({ row: rowNumber, field: 'Roll Number', message: `Duplicate Roll Number ${rollNumber} in Class ${studentClass}${section}` });
            } else {
              rollNumbersInFile.set(classKey, rowNumber);
            }
          }

          if (!name) {
            errors.push({ row: rowNumber, field: 'Name', message: 'Required field' });
          }
          if (!studentClass) {
            errors.push({ row: rowNumber, field: 'Class', message: 'Required field' });
          }
          if (!section) {
            errors.push({ row: rowNumber, field: 'Section', message: 'Required field' });
          }

          // Create student record
          const student: StudentRecord = {
            enrollmentId,
            rollNumber,
            name,
            class: studentClass,
            section,
            house: row['House'] ? String(row['House']).trim() : undefined,
            email: row['Email'] ? String(row['Email']).trim() : undefined,
            phone: row['Phone'] ? String(row['Phone']).trim() : undefined,
            dateOfBirth: row['Date of Birth (YYYY-MM-DD)'] ? String(row['Date of Birth (YYYY-MM-DD)']).trim() : undefined,
            gender: row['Gender (male/female/other)'] ? String(row['Gender (male/female/other)']).trim().toLowerCase() : undefined,
            address: row['Address'] ? String(row['Address']).trim() : undefined,
            parentName: row['Parent Name'] ? String(row['Parent Name']).trim() : undefined,
            parentPhone: row['Parent Phone'] ? String(row['Parent Phone']).trim() : undefined,
            parentEmail: row['Parent Email'] ? String(row['Parent Email']).trim() : undefined,
            bloodGroup: row['Blood Group'] ? String(row['Blood Group']).trim() : undefined,
            transportAvailed: row['School Transport Availed (Y/N)'] ? String(row['School Transport Availed (Y/N)']).trim().toUpperCase() : undefined,
            aadhaarNumber: row['Aadhaar Number'] ? String(row['Aadhaar Number']).trim() : undefined,
          };

          parsedStudents.push(student);
        });

        setStudents(parsedStudents);
        setValidationErrors(errors);

        if (errors.length > 0) {
          console.warn('Validation errors found:', errors);
        } else {
          console.log('All records validated successfully');
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setValidationErrors([{ row: 0, field: 'File', message: 'Failed to parse Excel file. Please check the format.' }]);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const uploadStudents = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before uploading');
      return;
    }

    if (students.length === 0) {
      alert('No students to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadedCount(0);

    try {
      // Check for existing students in database
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const existingEnrollmentIds = new Set<string>();
      const existingRollNumbers = new Set<string>(); // Format: class-section-rollnumber

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.enrollmentId) {
          existingEnrollmentIds.add(data.enrollmentId);
        }
        if (data.class && data.section && data.rollNumber) {
          existingRollNumbers.add(`${data.class}-${data.section}-${data.rollNumber}`);
        }
      });

      // Filter out duplicates
      const duplicates: string[] = [];
      const studentsToUpload = students.filter((student) => {
        const enrollmentDuplicate = existingEnrollmentIds.has(student.enrollmentId);
        const rollNumberKey = `${student.class}-${student.section}-${student.rollNumber}`;
        const rollNumberDuplicate = existingRollNumbers.has(rollNumberKey);

        if (enrollmentDuplicate) {
          duplicates.push(`Enrollment ID: ${student.enrollmentId} (${student.name})`);
          return false;
        }
        if (rollNumberDuplicate) {
          duplicates.push(`Roll No: ${student.rollNumber} in Class ${student.class}${student.section} (${student.name})`);
          return false;
        }
        return true;
      });

      if (duplicates.length > 0) {
        const confirmMsg = `Found ${duplicates.length} duplicate record(s) in database:\n\n${duplicates.slice(0, 5).join('\n')}${duplicates.length > 5 ? `\n...and ${duplicates.length - 5} more` : ''}\n\nThese records will be skipped. Continue with ${studentsToUpload.length} new records?`;

        if (!confirm(confirmMsg)) {
          setUploading(false);
          return;
        }
      }

      if (studentsToUpload.length === 0) {
        alert('All records already exist in the database. No new students to upload.');
        setUploading(false);
        return;
      }

      // Firestore batch can handle up to 500 operations
      const batchSize = 500;
      const batches = [];

      for (let i = 0; i < studentsToUpload.length; i += batchSize) {
        batches.push(studentsToUpload.slice(i, i + batchSize));
      }

      let totalUploaded = 0;

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = writeBatch(db);
        const currentBatch = batches[batchIndex];

        currentBatch.forEach((student) => {
          // Use enrollment ID as document ID
          const docId = student.enrollmentId;
          const studentDocRef = doc(collection(db, `school-users/${domain}/students`), docId);

          // Remove undefined fields and add className field
          const studentData = Object.fromEntries(
            Object.entries(student).filter(([_, value]) => value !== undefined)
          );

          // Add className field in format "Class 1A"
          studentData.className = `Class ${student.class}${student.section}`;

          // Write student data to school-users/{domain}/students
          batch.set(studentDocRef, studentData);

          // Create user profile in /users collection with enrollmentId as document ID
          const userDocRef = doc(db, 'users', student.enrollmentId);

          // Format documentId as: student_{class}_{section}_{rollNumber}
          const formattedDocId = `student_${student.class}_${student.section}_${student.rollNumber.padStart(5, '0')}`;

          const userProfileData = {
            created_at: Timestamp.now(),
            custom_user_id: student.enrollmentId,
            documentId: formattedDocId,
            email: student.email || `${student.enrollmentId.toLowerCase()}@${domain}.com`,
            role: 'student',
            userId: student.enrollmentId,
          };

          // Write user profile to /users/{enrollmentId}
          batch.set(userDocRef, userProfileData);
        });

        await batch.commit();
        totalUploaded += currentBatch.length;
        setUploadedCount(totalUploaded);
        setUploadProgress((totalUploaded / studentsToUpload.length) * 100);
      }

      setUploadComplete(true);
      const message = duplicates.length > 0
        ? `✅ Successfully uploaded ${totalUploaded} students. ${duplicates.length} duplicate(s) were skipped.`
        : `✅ Successfully uploaded ${totalUploaded} students`;
      console.log(message);
      alert(message);
    } catch (error: any) {
      console.error('Error uploading students:', error);
      alert(`Failed to upload students: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setStudents([]);
    setValidationErrors([]);
    setUploadComplete(false);
    setUploadProgress(0);
    setUploadedCount(0);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-upload me-2 text-primary-custom"></i>
          Bulk Upload Students
        </h5>
        <small className="text-muted">Upload multiple students using Excel file</small>
      </div>
      <div className="card-body">
        {/* Download Template Section */}
        <div className="alert alert-info d-flex align-items-center mb-4">
          <i className="bi bi-info-circle fs-4 me-3"></i>
          <div className="flex-grow-1">
            <strong>Step 1:</strong> Download the Excel template, fill in student details, and upload the file.
            <br />
            <small>Fields marked with * are required.</small>
          </div>
          <button className="btn btn-primary" onClick={downloadTemplate}>
            <i className="bi bi-download me-2"></i>
            Download Template
          </button>
        </div>

        {/* File Upload Section */}
        <div className="mb-4">
          <label className="form-label fw-semibold">
            <i className="bi bi-file-earmark-excel me-2"></i>
            Step 2: Upload Excel File
          </label>
          <input
            type="file"
            className="form-control"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && (
            <small className="text-muted d-block mt-2">
              <i className="bi bi-check-circle text-success me-1"></i>
              Selected: {file.name}
            </small>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="alert alert-danger">
            <h6 className="alert-heading">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Validation Errors ({validationErrors.length})
            </h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Field</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {validationErrors.map((error, idx) => (
                    <tr key={idx}>
                      <td>{error.row}</td>
                      <td>{error.field}</td>
                      <td>{error.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Preview Section */}
        {students.length > 0 && validationErrors.length === 0 && (
          <div className="mb-4">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-eye me-2 text-success"></i>
              Preview ({students.length} students)
            </h6>
            <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <table className="table table-sm table-bordered">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Enrollment ID</th>
                    <th>Roll No</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>House</th>
                    <th>Transport</th>
                    <th>Aadhaar</th>
                    <th>Email</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, idx) => (
                    <tr key={idx}>
                      <td>{student.enrollmentId}</td>
                      <td>{student.rollNumber}</td>
                      <td>{student.name}</td>
                      <td>{student.class}</td>
                      <td>{student.section}</td>
                      <td>{student.house || '-'}</td>
                      <td>
                        {student.transportAvailed ? (
                          <span className={`badge ${student.transportAvailed === 'Y' ? 'bg-success' : 'bg-secondary'}`}>
                            {student.transportAvailed === 'Y' ? 'Yes' : 'No'}
                          </span>
                        ) : '-'}
                      </td>
                      <td>{student.aadhaarNumber || '-'}</td>
                      <td>{student.email || '-'}</td>
                      <td>{student.phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-2">
              <span>Uploading students...</span>
              <span>{uploadedCount} / {students.length}</span>
            </div>
            <div className="progress" style={{ height: '25px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
              >
                {Math.round(uploadProgress)}%
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {uploadComplete && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle me-2"></i>
            Successfully uploaded {uploadedCount} students to <strong>{domain}</strong>!
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          {!uploadComplete && students.length > 0 && validationErrors.length === 0 && (
            <button
              className="btn btn-success"
              onClick={uploadStudents}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Upload {students.length} Students
                </>
              )}
            </button>
          )}
          {(students.length > 0 || uploadComplete) && (
            <button className="btn btn-secondary" onClick={reset} disabled={uploading}>
              <i className="bi bi-arrow-counterclockwise me-2"></i>
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
